import React, { useMemo } from "react";
import styled, { keyframes } from "styled-components";

/**
 * Fondo decorativo para bingo
 * Capas:
 * 1) Rayos giratorios (más atrás)
 * 2) Monedas cayendo
 * 3) Balotas flotando (por delante de las monedas)
 *
 * No interfiere con clicks.
 */
const BingoBackground = ({
  coinCount = 16,
  ballCount = 14,
}) => {
  const coins = useMemo(() => {
    return Array.from({ length: coinCount }).map((_, i) => {
      const left = Math.random() * 100;
      const size = 24 + Math.random() * 40;
      const duration = 6 + Math.random() * 8;
      const delay = Math.random() * 6;
      const drift = (Math.random() * 2 - 1) * 60;
      const spin = 360 + Math.random() * 720;

      return { id: i, left, size, duration, delay, drift, spin };
    });
  }, [coinCount]);

  const balls = useMemo(() => {
    const letters = ["B", "I", "N", "G", "O"];
    const isDesktop =
      typeof window !== "undefined" ? window.innerWidth >= 1024 : true;

    const safeMargin = isDesktop ? 18 : 12; // margen para que no se corten
    const centerStart = 34; // inicio franja central prohibida
    const centerEnd = 66;   // fin franja central prohibida

    const minSize = isDesktop ? 64 : 52;
    const maxSize = isDesktop ? 92 : 70;

    const placedBalls = [];

    const randomBetween = (min, max) => Math.random() * (max - min) + min;

    const overlapsExisting = (candidate) => {
      return placedBalls.some((ball) => {
        const dx = candidate.left - ball.left;
        const dy = candidate.top - ball.top;
        const distance = Math.sqrt(dx * dx + dy * dy);

        // distancia mínima según tamaño de ambas
        const minDistance =
          ((candidate.size + ball.size) / 2) * 0.18 + (isDesktop ? 2.5 : 2);

        return distance < minDistance;
      });
    };

    const getCandidatePosition = (size) => {
      const radiusPercentX = (size / window.innerWidth) * 50;
      const radiusPercentY = (size / window.innerHeight) * 50;

      const topMin = safeMargin / 2 + radiusPercentY;
      const topMax = 100 - safeMargin / 2 - radiusPercentY;

      const side = Math.random() < 0.5 ? "left" : "right";

      let left;
      if (side === "left") {
        left = randomBetween(6 + radiusPercentX, centerStart - 4 - radiusPercentX);
      } else {
        left = randomBetween(centerEnd + 4 + radiusPercentX, 94 - radiusPercentX);
      }

      const top = randomBetween(topMin, topMax);

      return { top, left };
    };

    return Array.from({ length: ballCount }).map((_, i) => {
      const number = 1 + Math.floor(Math.random() * 50);
      const letter = letters[Math.floor(Math.random() * letters.length)];
      const size = randomBetween(minSize, maxSize);

      let candidate = null;
      let tries = 0;

      while (tries < 80) {
        const pos = getCandidatePosition(size);

        candidate = {
          id: i,
          top: pos.top,
          left: pos.left,
          size,
          duration: 5 + Math.random() * 8,
          delay: Math.random() * 5,
          driftX: (Math.random() * 2 - 1) * (isDesktop ? 10 : 7),
          driftY: (Math.random() * 2 - 1) * (isDesktop ? 8 : 6),
          rotate: (Math.random() * 2 - 1) * (isDesktop ? 7 : 5),
          number,
          letter,
        };

        if (!overlapsExisting(candidate)) {
          placedBalls.push(candidate);
          break;
        }

        tries++;
      }

      // fallback por si no encuentra hueco
      if (!candidate) {
        candidate = {
          id: i,
          top: randomBetween(15, 85),
          left: Math.random() < 0.5 ? randomBetween(8, 26) : randomBetween(74, 92),
          size,
          duration: 5 + Math.random() * 8,
          delay: Math.random() * 5,
          driftX: (Math.random() * 2 - 1) * (isDesktop ? 10 : 7),
          driftY: (Math.random() * 2 - 1) * (isDesktop ? 8 : 6),
          rotate: (Math.random() * 2 - 1) * (isDesktop ? 7 : 5),
          number,
          letter,
        };
        placedBalls.push(candidate);
      }

      return candidate;
    });
  }, [ballCount]);

  return (
    <Layer aria-hidden="true">
      <Glow />

      <RaysLayer>
        <RayDisc />
        <RayDiscSoft />
        <CenterGlow />
      </RaysLayer>

      <CoinsLayer>
        {coins.map((c) => (
          <Coin
            key={`coin-a-${c.id}`}
            style={{
              left: `${c.left}vw`,
              width: `${c.size}px`,
              height: `${c.size}px`,
              ["--drift"]: `${c.drift}px`,
              ["--spin"]: `${c.spin}deg`,
              animationDuration: `${c.duration}s`,
              animationDelay: `${-c.delay}s`,
            }}
          />
        ))}

        {coins.map((c) => (
          <Coin
            key={`coin-b-${c.id}`}
            style={{
              left: `${c.left}vw`,
              width: `${c.size}px`,
              height: `${c.size}px`,
              ["--drift"]: `${c.drift}px`,
              ["--spin"]: `${c.spin}deg`,
              animationDuration: `${c.duration}s`,
              animationDelay: `${-(c.delay + c.duration / 2)}s`,
            }}
          />
        ))}
      </CoinsLayer>

      {/* <BallsLayer>
        {balls.map((b) => (
          <Ball
            key={b.id}
            style={{
              top: `${b.top}%`,
              left: `${b.left}%`,
              width: `${b.size}px`,
              height: `${b.size}px`,
              ["--dx"]: `${b.driftX}px`,
              ["--dy"]: `${b.driftY}px`,
              ["--rot"]: `${b.rotate}deg`,
              animationDuration: `${b.duration}s`,
              animationDelay: `${-b.delay}s`,
            }}
          >
            <BallInner>
              <BallShine />
              <BallNumberPlate>
                <BallLetter>{b.letter}</BallLetter>
                <BallNumber>{b.number}</BallNumber>
              </BallNumberPlate>
            </BallInner>
          </Ball>
        ))}
      </BallsLayer> */}
    </Layer>
  );
};

export default BingoBackground;

/* ===================== Animaciones ===================== */

const rotateSlow = keyframes`
  from { transform: translate(-50%, -50%) rotate(0deg); }
  to   { transform: translate(-50%, -50%) rotate(360deg); }
`;

const rotateReverse = keyframes`
  from { transform: translate(-50%, -50%) rotate(360deg); }
  to   { transform: translate(-50%, -50%) rotate(0deg); }
`;

const pulseGlow = keyframes`
  0%, 100% { opacity: 0.55; transform: translate(-50%, -50%) scale(1); }
  50% { opacity: 0.82; transform: translate(-50%, -50%) scale(1.06); }
`;

const fall = keyframes`
  0% {
    transform: translate3d(0, -120px, 0) rotate(0deg);
  }
  100% {
    transform: translate3d(var(--drift), calc(100vh + 160px), 0) rotate(var(--spin));
  }
`;

const floatCoin = keyframes`
  0%, 100% { filter: drop-shadow(0 12px 18px rgba(0,0,0,0.22)); }
  50% { filter: drop-shadow(0 18px 28px rgba(0,0,0,0.3)); }
`;

const ballDrift = keyframes`
  0% {
    transform: translate(-50%, -50%) translate3d(0, 0, 0) rotate(0deg) scale(1);
  }
  25% {
    transform: translate(-50%, -50%)
      translate3d(calc(var(--dx) * .55), calc(var(--dy) * -.35), 0)
      rotate(calc(var(--rot) * .45)) scale(1.02);
  }
  50% {
    transform: translate(-50%, -50%)
      translate3d(var(--dx), var(--dy), 0)
      rotate(var(--rot)) scale(1);
  }
  75% {
    transform: translate(-50%, -50%)
      translate3d(calc(var(--dx) * -.45), calc(var(--dy) * .35), 0)
      rotate(calc(var(--rot) * -.35)) scale(.985);
  }
  100% {
    transform: translate(-50%, -50%) translate3d(0, 0, 0) rotate(0deg) scale(1);
  }
`;

/* ===================== Estilos base ===================== */

const Layer = styled.div`
  position: absolute;
  inset: 0;
  overflow: hidden;
  pointer-events: none;
  z-index: 0;
`;

const Glow = styled.div`
  position: absolute;
  inset: 0;
  background:
    radial-gradient(circle at center, rgba(255, 200, 0, 0.08) 0%, rgba(255, 160, 0, 0.04) 25%, rgba(0, 0, 0, 0) 55%),
    radial-gradient(circle at top, rgba(255, 255, 255, 0.02), transparent 45%);
`;

/* ===================== Rayos ===================== */

const RaysLayer = styled.div`
  position: absolute;
  inset: 0;
  z-index: 0;
`;

const RayDisc = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  width: min(120vmax, 1600px);
  aspect-ratio: 1 / 1;
  transform: translate(-50%, -50%);
  border-radius: 50%;
  opacity: 0.22;
  animation: ${rotateSlow} 80s linear infinite;
  background:
    repeating-conic-gradient(
      from 0deg,
      rgba(255, 204, 0, 0.18) 0deg 10deg,
      rgba(255, 120, 0, 0.07) 10deg 20deg
    );
  filter: blur(1px);
`;

const RayDiscSoft = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  width: min(105vmax, 1400px);
  aspect-ratio: 1 / 1;
  transform: translate(-50%, -50%);
  border-radius: 50%;
  opacity: 0.12;
  animation: ${rotateReverse} 120s linear infinite;
  background:
    repeating-conic-gradient(
      from 6deg,
      rgba(255, 255, 255, 0.08) 0deg 7deg,
      rgba(255, 180, 0, 0.02) 7deg 18deg
    );
  filter: blur(3px);
`;

const CenterGlow = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  width: clamp(220px, 34vw, 560px);
  height: clamp(220px, 34vw, 560px);
  border-radius: 50%;
  transform: translate(-50%, -50%);
  background: radial-gradient(
    circle,
    rgba(255, 224, 130, 0.28) 0%,
    rgba(255, 183, 77, 0.18) 35%,
    rgba(255, 111, 0, 0.08) 58%,
    rgba(255, 111, 0, 0) 75%
  );
  animation: ${pulseGlow} 8s ease-in-out infinite;
`;

/* ===================== Monedas ===================== */

const CoinsLayer = styled.div`
  position: absolute;
  inset: 0;
  z-index: 1;
`;

const Coin = styled.span`
  position: absolute;
  top: -140px;
  border-radius: 999px;

  /* color sólido */
  background: radial-gradient(
    circle at 32% 28%,
    #fff2a6 0%,
    #ffd24a 18%,
    #f4b400 48%,
    #d99200 75%,
    #b87400 100%
  );

  border: 2px solid #ffd95e;

  box-shadow:
    inset 0 8px 14px rgba(255, 255, 255, 0.4),
    inset 0 -8px 14px rgba(120, 70, 0, 0.28),
    0 10px 20px rgba(0, 0, 0, 0.2);

  &::before {
    content: "";
    position: absolute;
    inset: 10%;
    border-radius: 50%;
    border: 2px solid rgba(255, 237, 167, 0.7);
  }

  &::after {
    content: "$";
    position: absolute;
    inset: 0;
    display: grid;
    place-items: center;
    font-weight: 900;
    color: #7a4a00;
    font-size: clamp(14px, 2.4vw, 20px);
    text-shadow: 0 1px 0 rgba(255, 255, 255, 0.5);
  }

  animation: ${fall} linear infinite, ${floatCoin} ease-in-out 2.6s infinite;
`;

/* ===================== Balotas ===================== */

const BallsLayer = styled.div`
  position: absolute;
  inset: 0;
  z-index: 2;
`;

const Ball = styled.div`
  position: absolute;
  border-radius: 50%;
  transform: translate(-50%, -50%);
  animation: ${ballDrift} ease-in-out infinite;
  will-change: transform;
`;

const BallInner = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  overflow: hidden;

  /* color más sólido y con sensación de esfera */
  background: radial-gradient(
    circle at 30% 26%,
    #fff5f5 0%,
    #ffb3b3 14%,
    #ff4d4d 42%,
    #d62828 70%,
    #8f1111 100%
  );

  box-shadow:
    inset -12px -16px 20px rgba(120, 35, 0, 0.28),
    inset 10px 12px 20px rgba(255, 255, 255, 0.38),
    0 14px 30px rgba(0, 0, 0, 0.2);
`;

const BallShine = styled.div`
  position: absolute;
  top: 12%;
  left: 16%;
  width: 34%;
  height: 24%;
  border-radius: 50%;
  background: radial-gradient(
    ellipse at center,
    rgba(255, 255, 255, 0.95) 0%,
    rgba(255, 255, 255, 0.55) 45%,
    rgba(255, 255, 255, 0) 100%
  );
  transform: rotate(-18deg);
  filter: blur(1px);
`;

const BallNumberPlate = styled.div`
  position: absolute;
  inset: 22%;
  border-radius: 50%;
  background: radial-gradient(
    circle at 30% 30%,
    #ffffff 0%,
    #fffaf0 65%,
    #f5e6c8 100%
  );
  border: 2px solid rgba(180, 110, 0, 0.2);
  box-shadow:
    inset 0 4px 8px rgba(255, 255, 255, 0.65),
    inset 0 -4px 8px rgba(0, 0, 0, 0.08);

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const BallLetter = styled.span`
  font-size: clamp(10px, 1vw, 12px);
  font-weight: 900;
  line-height: 1;
  color: #a54b00;
  letter-spacing: 0.08em;
  margin-bottom: 2px;
`;

const BallNumber = styled.span`
  font-size: clamp(14px, 1.4vw, 22px);
  font-weight: 900;
  line-height: 1;
  color: #5f2400;
`;